// Angular
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Route, Router } from '@angular/router';

// Libraries
import { Observable, takeUntil } from 'rxjs';
import { Select } from '@ngxs/store';
import { MatMenuTrigger } from '@angular/material/menu';

// Services
import { JobService } from '../../../modules/job/services/job.service';

// Models
import { AppCleanup } from '../../models';
import { HorizontalItem } from '../../models/horizontal-item.model';
import { Job, JobUpdate } from '../../../modules/job/models';

// Store
import { StorageState } from '../../state/storage.state';

// Enums
import { JobType, UnitType } from '../../enums';

// Helpers
import { UtilityHelper } from '../../helpers';

@Component({
	selector: 'header-menu',
	templateUrl: './header-menu.component.html',
	styleUrl: './header-menu.component.scss',
})
export class HeaderMenuComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(StorageState.jobRouteParams) jobRouteParams$: Observable<Params>;
	@Input() menuList: HorizontalItem[];
	jobId: number;
	job: Job;
	disableForceMonitor: boolean; // Disable Force Monitor if Depth and Weight are missing
	contextMenuPosition = { x: '0px', y: '0px' };
	@ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

	constructor(private jobService: JobService) {
		super();
	}

	ngOnInit() {
		this.jobRouteParams$.pipe(takeUntil(this.destroy)).subscribe((params) => {
			if (params) {
				this.jobId = Number(params.jobId);
				this.jobService
					.getJobStream(String(this.jobId))
					.pipe(takeUntil(this.destroy))
					.subscribe((jobUpdate: JobUpdate) => {
						if (jobUpdate) {
							this.job = jobUpdate.job;

							const unitPeriodsWithDepth: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
								.filter((upc) => upc.channel.label.split(' ').includes('Depth'))
								.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
							const unitPeriodsWithWeight: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
								.filter((upc) => upc.channel.label.split(' ').includes('Weight'))
								.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
							const unitPeriodsWithDepthAndWeight = unitPeriodsWithDepth.filter((up) => unitPeriodsWithWeight.some((up2) => up2.unitPeriodId == up.unitPeriodId));
							const unitPeriodsWithDepthAndWeightAndCoiledTubing = unitPeriodsWithDepthAndWeight.filter((up) => up.unitTypeId == UnitType.CoiledTubing);
							let forceMonitorUnitPeriodId =
								unitPeriodsWithDepthAndWeightAndCoiledTubing.length > 0
									? unitPeriodsWithDepthAndWeightAndCoiledTubing[0].unitPeriodId
									: unitPeriodsWithDepthAndWeight.length > 0
										? unitPeriodsWithDepthAndWeight[0].unitPeriodId
										: undefined;

							if (this.job.jobType.id !== UnitType.CoiledTubing || !forceMonitorUnitPeriodId) {
								this.disableForceMonitor = true;
							} else {
								this.disableForceMonitor = false;
							}
						}
					});

				// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
				// 	if (!this.job && jobChange && jobChange.job?.key == String(this.jobId)) {
				// 		this.job = jobChange.job;

				// 		const unitPeriodsWithDepth: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
				// 			.filter((upc) => upc.channel.label.split(' ').includes('Depth'))
				// 			.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
				// 		const unitPeriodsWithWeight: { unitPeriodId: number; unitTypeId: number }[] = this.job.unitPeriodChannels
				// 			.filter((upc) => upc.channel.label.split(' ').includes('Weight'))
				// 			.map((upc) => ({ unitPeriodId: upc.unitPeriodId, unitTypeId: upc.unit.unitTypeId }));
				// 		const unitPeriodsWithDepthAndWeight = unitPeriodsWithDepth.filter((up) => unitPeriodsWithWeight.some((up2) => up2.unitPeriodId == up.unitPeriodId));
				// 		const unitPeriodsWithDepthAndWeightAndCoiledTubing = unitPeriodsWithDepthAndWeight.filter((up) => up.unitTypeId == UnitType.CoiledTubing);
				// 		let forceMonitorUnitPeriodId =
				// 			unitPeriodsWithDepthAndWeightAndCoiledTubing.length > 0
				// 				? unitPeriodsWithDepthAndWeightAndCoiledTubing[0].unitPeriodId
				// 				: unitPeriodsWithDepthAndWeight.length > 0
				// 					? unitPeriodsWithDepthAndWeight[0].unitPeriodId
				// 					: undefined;

				// 		if (this.job.jobType.id !== UnitType.CoiledTubing || !forceMonitorUnitPeriodId) {
				// 			this.disableForceMonitor = true;
				// 		} else {
				// 			this.disableForceMonitor = false;
				// 		}
				// 	}
				// });
			} else {
				this.jobId = null;
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	onContextMenu(event: MouseEvent, item: any) {
		event.preventDefault();
		this.contextMenuPosition.x = event.clientX + 'px';
		this.contextMenuPosition.y = event.clientY + 'px';
		this.contextMenu.menuData = { item: item };
		this.contextMenu.menu.focusFirstItem('mouse');
		this.contextMenu.openMenu();
	}

	openInNewTab(item: HorizontalItem) {
		UtilityHelper.openInNewTab(item.page);
	}

	openInNewWindow(item: HorizontalItem) {
		UtilityHelper.openInNewWindow(item.page);
	}

	isDisabled(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				return true;
			}
		}

		return false;
	}

	disableTooltip(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				return false;
			}
		}

		return true;
	}

	getTooltip(item: HorizontalItem) {
		if (item.title === 'Force Monitor') {
			if (this.disableForceMonitor) {
				if (this.job.jobType.id !== JobType.CoiledTubing) {
					return "Force Monitor is only available for job type 'Coiled Tubing'";
				} else {
					return "Can't use Force Monitor without depth and weight channels";
				}
			}
		}

		return '';
	}
}
